import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  Button,
  FormField,
  SubmitButton,
  Textarea,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { portalPackagesApi } from "../api/packages";
import type { PortalItemDto } from "../api/types";
import { staffNoteFor } from "../lib/itemStatus";

// Review loop — the Respond step. When staff return an item, the recipient owes
// a response (`item.responseOwed`): a short note, plus either "Send back
// without changes" (only while `canSendBackWithoutChanges`; no signature step)
// or "Make changes", which continues into the kind's normal path. The SAME note
// travels with that path's final recipient action (submitForm, confirmUpload,
// submitInHouseSignature, submitEducation, startESign) and stays visible and
// editable until then; final actions are disabled while it is blank.
//
// Every item surface is rendered through this wrapper. When no response is
// owed it renders the surface unchanged with `response = null`, so items that
// never get `responseOwed` (Report Request packages) look exactly as before.

export const RESPONSE_NOTE_MAX_LENGTH = 4000;

/** What a fulfilment surface needs to finish an item that owes a response. */
export interface PortalResponse {
  /** The trimmed note, sent with the surface's final action. */
  note: string;
  /** Why the final action must stay disabled, or null when the note is valid. */
  blockedReason: string | null;
  /** Call once the note has been handed to the server by an action that does
   *  not itself finish the item (DocuSign start): the editor locks. */
  onCommitted: () => void;
}

/** Why `raw` can't be sent as a response note, or null when it can. Mirrors
 *  the server rule: required, non-blank, at most 4,000 characters. */
export function responseNoteProblem(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return "Add a short note explaining your response first.";
  if (trimmed.length > RESPONSE_NOTE_MAX_LENGTH) {
    return "Your note is too long — keep it to 4,000 characters or fewer.";
  }
  return null;
}

/** The reason a final action is disabled, shown beside that action. */
export function ResponseBlockedHint({ response }: { response: PortalResponse | null | undefined }) {
  if (!response?.blockedReason) return null;
  return (
    <p className="text-caption text-status-warning m-0" data-testid="response-blocked-reason">
      {response.blockedReason}
    </p>
  );
}

interface ResponseStepProps {
  item: PortalItemDto;
  onUpdated: (next: PortalItemDto) => void;
  /** Renders the kind's surface; `response` is null when none is owed. */
  children: (response: PortalResponse | null) => ReactNode;
}

export function ResponseStep({ item, onUpdated, children }: ResponseStepProps) {
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState<"choose" | "changes">("choose");
  const [committed, setCommitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = note.trim();
  const problem = responseNoteProblem(note);
  const onCommitted = useCallback(() => setCommitted(true), []);
  const response = useMemo<PortalResponse>(
    () => ({ note: trimmed, blockedReason: problem, onCommitted }),
    [trimmed, problem, onCommitted],
  );

  const sendBack = useSubmittingMutation(
    async () => {
      const next = await portalPackagesApi.respondWithoutChanges(item.id, trimmed);
      onUpdated(next);
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Couldn't send this back. Try again in a moment."),
    },
  );

  const owed = item.responseOwed === true;
  const choosing = owed && phase === "choose";
  const tooLong = trimmed.length > RESPONSE_NOTE_MAX_LENGTH;

  const noteField = (
    <FormField
      label="Your response note"
      required
      error={tooLong && problem ? problem : undefined}
      helperText={`Tell the Phoenix team what you changed, or why nothing needs to change. ${trimmed.length.toLocaleString("en-US")} of 4,000 characters.`}
    >
      <Textarea
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          setError(null);
        }}
        disabled={sendBack.pending}
        invalid={tooLong}
        rows={3}
        data-testid="response-note"
      />
    </FormField>
  );

  // Fixed slots (panel · editor · surface) so the surface keeps its position —
  // and its state — when the response is sent and `owed` flips to false.
  return (
    <>
      {choosing ? (
        <section
          className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-lg bg-white-pure"
          data-testid="respond-panel"
        >
          <h2 className="text-lead font-display text-onyx m-0">Respond to the Phoenix team</h2>
          <p className="text-small text-gray-mid m-0">
            {staffNoteFor(item)
              ? "Your Phoenix contact sent this item back with the note above."
              : "Your Phoenix contact sent this item back."}{" "}
            Add a short reply, then choose how to respond.
          </p>
          {noteField}
          <div className="flex flex-wrap items-center gap-space-2">
            <Button
              onClick={() => setPhase("changes")}
              disabled={problem !== null || sendBack.pending}
              data-testid="respond-make-changes"
            >
              Make changes
            </Button>
            {item.canSendBackWithoutChanges && (
              <SubmitButton
                variant="secondary"
                onClick={() => {
                  setError(null);
                  void sendBack.submit();
                }}
                pending={sendBack.pending}
                pendingLabel="Sending…"
                disabled={problem !== null}
                data-testid="respond-send-back"
              >
                Send back without changes
              </SubmitButton>
            )}
          </div>
          {problem && !tooLong && (
            <p className="text-caption text-gray-mid m-0" data-testid="response-blocked-reason">
              {problem}
            </p>
          )}
          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
        </section>
      ) : null}

      {owed && phase === "changes" ? (
        <section
          className="flex flex-col gap-space-2 p-space-4 border border-border-light rounded-lg bg-white-pure"
          data-testid="response-note-editor"
        >
          {committed ? (
            <>
              <p className="text-small font-medium text-onyx m-0">Your response note</p>
              <p className="text-small text-onyx m-0 whitespace-pre-wrap" data-testid="response-note-committed">
                {trimmed}
              </p>
              <p className="text-caption text-gray-mid m-0">
                This note goes to the Phoenix team when signing finishes.
              </p>
            </>
          ) : (
            <>
              {noteField}
              <p className="text-caption text-gray-mid m-0">
                This note goes to the Phoenix team with your updated item.
              </p>
            </>
          )}
        </section>
      ) : null}

      {choosing ? null : children(owed ? response : null)}
    </>
  );
}
